import type { Directive } from 'vue';

const LAZY_ATTRIBUTES = ['src', 'srcset', 'poster'];

const onVisible = new WeakMap<Element, () => void>();
let observer: IntersectionObserver | null = null;

function unobserve(el: Element) {
  onVisible.delete(el);
  observer?.unobserve(el);
}

function observeOnce(el: Element, callback: () => void) {
  observer ??= new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      const load = onVisible.get(target);
      if (!isIntersecting || !load) return;

      unobserve(target);
      load();
    });
  });

  unobserve(el);
  onVisible.set(el, callback);
  observer.observe(el);
}

const lazyTargets = (el: HTMLElement) => [
  el,
  ...(Array.from(el.children) as HTMLElement[]),
];

const hasLazyData = (el: HTMLElement) =>
  lazyTargets(el).some((target) =>
    LAZY_ATTRIBUTES.some((attribute) => target.dataset[attribute]),
  );

function applyLazyData(el: HTMLElement) {
  lazyTargets(el).forEach((target) => {
    LAZY_ATTRIBUTES.forEach((attribute) => {
      const value = target.dataset[attribute];
      if (!value) return;

      target.setAttribute(attribute, value);
      target.removeAttribute(`data-${attribute}`);
    });
  });

  if (el instanceof HTMLMediaElement) el.load();
}

const lazyLoad: Directive<HTMLElement> = {
  mounted(el) {
    if (hasLazyData(el)) observeOnce(el, () => applyLazyData(el));
  },

  updated(el) {
    if (onVisible.has(el) || !hasLazyData(el)) return;

    observeOnce(el, () => applyLazyData(el));
  },

  unmounted: unobserve,
};

const setBackground = (el: HTMLElement, url?: string) => {
  if (!url) return;

  observeOnce(el, () => {
    el.style.setProperty('background-image', `url(${url})`);
  });
};

const lazyBackground: Directive<HTMLElement, string | undefined> = {
  mounted(el, { value }) {
    setBackground(el, value);
  },

  updated(el, { value, oldValue }) {
    if (value === oldValue) return;

    unobserve(el);
    setBackground(el, value);
  },

  unmounted: unobserve,
};

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('lazy-load', lazyLoad);
  nuxtApp.vueApp.directive('lazy-background', lazyBackground);
});
